import React, { useEffect, useMemo, useState } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Gift, Users, Share2, Cake, Save, RefreshCw, Mail, Sparkles, Plus, Trash2, Award, ChevronDown, ChevronRight } from "lucide-react";

/*
 * Brand-Level Loyalty Settings
 * -----------------------------
 * A single global panel that owns every EARN rule for the whole
 * company. Birthday / Join / Referral rewards are set per branch —
 * there is no "default" fallback so a manager only sees and edits
 * the branch numbers that matter to them. Spend-to-Points keeps a
 * per-branch currency-per-point too. Redemption VALUE per point stays
 * branch-level (bottom card) so each currency prices its own point at
 * checkout.
 */

/**
 * Number input that renders an empty string when the value is 0 so
 * typing "200" never sticks as "0200". Uses text input to give us
 * total control over the display; digits-only enforcement keeps the
 * result numeric.
 */
function PointsInput({ value, onChange, disabled, placeholder = "0", testid, className = "" }) {
  const n = Number(value);
  const display = !Number.isFinite(n) || n === 0 ? "" : String(n);
  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={display}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => {
        const cleaned = (e.target.value || "").replace(/[^0-9]/g, "");
        // Strip leading zeros so "0200" becomes "200" before parsing.
        const trimmed = cleaned.replace(/^0+(?=\d)/, "");
        onChange(trimmed === "" ? 0 : Number(trimmed));
      }}
      className={className}
      data-testid={testid}
    />
  );
}

/**
 * Earn rule with per-branch point inputs. Used for the fixed-points
 * rules (Join / Birthday / Referral). Spend-to-Points now uses the
 * compact `SpendToggleRow` below because its per-branch Earn Rate
 * lives in the shared Branch Point Values master table.
 */
function BranchRule({ icon: Icon, title, description, rule, section, patch, branches, mapKey, unitLabelForBranch, testid, hideBranchInputs = false }) {
  const enabled = !!rule?.enabled;
  const overrides = rule?.[mapKey] || {};
  return (
    <div className="py-4 border-b border-slate-100 last:border-b-0" data-testid={`${testid}-row`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-700" /></div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-800" data-testid={`${testid}-title`}>{title}</div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">{description}</p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm shrink-0">
          <Switch checked={enabled} onCheckedChange={(v) => patch(section, { enabled: v })} data-testid={`${testid}-toggle`} />
          <span className="text-xs text-muted-foreground">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      {enabled && !hideBranchInputs && (
        <div className="mt-3 ml-12">
          {branches.length === 0 ? (
            <p className="text-xs text-muted-foreground">Add a branch first to set the reward.</p>
          ) : (
            <div className="grid gap-2 rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`${testid}-branches`}>
              {branches.map((br) => (
                <div key={br.id} className="grid grid-cols-[1fr_180px] gap-3 items-center">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{br.name}</div>
                    <p className="text-[10px] text-muted-foreground">{br.city || br.country || ""}{br.currency ? ` · ${br.currency}` : ""}</p>
                  </div>
                  <div>
                    <PointsInput
                      value={overrides[br.id] || 0}
                      onChange={(v) => {
                        const next = { ...overrides };
                        if (!v) delete next[br.id]; else next[br.id] = v;
                        patch(section, { [mapKey]: next });
                      }}
                      placeholder="0"
                      testid={`${testid}-branch-${br.id}`}
                    />
                    <p className="mt-1 text-[10px] text-muted-foreground text-right">{unitLabelForBranch(br)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/*
 * Brand-level Loyalty Expiry email card.
 * ---------------------------------------
 * Reads the single global template for `one_month_before_points_expiry`
 * from `GET /brand/email-templates/{trigger}` and PUTs subject + body
 * changes straight back. No branch context — one template governs the
 * look-and-feel for every member across every location.
 */
function BrandExpiryEmailCard() {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      const r = await staffApi.get("/brand/email-templates/one_month_before_points_expiry");
      setTpl(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const patch = (k, v) => setTpl((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      const r = await staffApi.put("/brand/email-templates/one_month_before_points_expiry", {
        subject: tpl.subject || "",
        body_starts_html: tpl.body_starts_html || "",
        body_ends_html: tpl.body_ends_html || "",
        enabled: tpl.enabled !== false,
      });
      setTpl(r.data);
      toast.success("Brand-level expiry email updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="brand-expiry-email-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-amber-50 text-amber-700">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="uppercase text-xs tracking-wider text-slate-600 font-semibold">Loyalty Expiry Warning · brand-wide email</div>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Single global template. Sent 30 days before any active point batch expires — the same subject + body goes to every member regardless of the branch that awarded the points. Placeholders: <code className="text-slate-800">{"{first_name}"}</code>, <code className="text-slate-800">{"{points}"}</code>, <code className="text-slate-800">{"{expiry_date}"}</code>.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={tpl?.enabled !== false}
                onCheckedChange={(v) => patch("enabled", v)}
                data-testid="brand-expiry-email-enabled"
                disabled={!tpl || busy || saving}
              />
              {tpl?.enabled === false ? "Disabled" : "Enabled"}
            </label>
            <Button variant="ghost" size="sm" onClick={reload} disabled={busy || saving} data-testid="brand-expiry-email-reload"><RefreshCw className="w-4 h-4 mr-1" />Reload</Button>
            <Button size="sm" onClick={save} disabled={!tpl || saving} data-testid="brand-expiry-email-save" className="bg-slate-800 hover:bg-slate-900 text-white">
              <Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {!tpl && <div className="text-sm text-muted-foreground">{busy ? "Loading brand template…" : "Template not available."}</div>}
        {tpl && (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Subject line</Label>
              <Input
                value={tpl.subject || ""}
                onChange={(e) => patch("subject", e.target.value)}
                className="mt-1"
                data-testid="brand-expiry-email-subject"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Opening paragraph (HTML)</Label>
              <Textarea
                value={tpl.body_starts_html || ""}
                onChange={(e) => patch("body_starts_html", e.target.value)}
                className="mt-1 font-mono text-xs"
                rows={4}
                data-testid="brand-expiry-email-body-starts"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Closing paragraph (HTML)</Label>
              <Textarea
                value={tpl.body_ends_html || ""}
                onChange={(e) => patch("body_ends_html", e.target.value)}
                className="mt-1 font-mono text-xs"
                rows={4}
                data-testid="brand-expiry-email-body-ends"
              />
            </div>
            <div className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              This template is brand-wide. No per-branch overrides — every member across every Cocoon location sees the same email.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/*
 * Reward Tiers · per branch
 * -------------------------
 * Migrated from the (now-deleted) Location Loyalty page. Each branch
 * publishes its own list of `{points, reward, note}` rows describing
 * what a member unlocks at each threshold. Data lives on the branch's
 * `loyalty_configs.tiers` array and is written by the parent's main
 * Save button (the existing per-branch PUT already ships `tiers`).
 */
function RewardTiersCard({ branches, branchCfgs, setBranchCfgs }) {
  const [openId, setOpenId] = useState(null);
  const patchTiers = (bid, next) =>
    setBranchCfgs((prev) => ({ ...prev, [bid]: { ...(prev[bid] || {}), tiers: next } }));
  const addTier = (bid) => {
    const cur = (branchCfgs[bid]?.tiers) || [];
    patchTiers(bid, [...cur, { points: 100, reward: "IDR 50.000 off any service", note: "" }]);
  };
  const updateTier = (bid, i, k, v) => {
    const cur = (branchCfgs[bid]?.tiers) || [];
    patchTiers(bid, cur.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  };
  const removeTier = (bid, i) => {
    const cur = (branchCfgs[bid]?.tiers) || [];
    patchTiers(bid, cur.filter((_, idx) => idx !== i));
  };
  return (
    <Card data-testid="brand-loyalty-tiers-card">
      <CardContent className="p-5">
        <div className="uppercase text-xs tracking-wider text-slate-600 font-semibold mb-1">Reward Tiers · per branch</div>
        <p className="text-xs text-muted-foreground mb-3 max-w-2xl">Define what points unlock at each branch. Members see their branch's ladder in the customer portal, and staff can reference it at checkout. Changes save with the main "Save changes" button above.</p>
        {branches.length === 0 && <p className="text-sm text-muted-foreground py-4">No branches yet.</p>}
        <div className="divide-y divide-slate-100">
          {branches.map((br) => {
            const tiers = (branchCfgs[br.id]?.tiers) || [];
            const isOpen = openId === br.id;
            return (
              <div key={br.id} className="py-3" data-testid={`brand-loyalty-tiers-branch-${br.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : br.id)}
                  className="w-full flex items-center justify-between gap-2 text-left hover:bg-slate-50/60 rounded-md px-2 py-2 -mx-2"
                  data-testid={`brand-loyalty-tiers-toggle-${br.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{br.name}</div>
                      <p className="text-[10px] text-muted-foreground truncate">{br.city || br.country || ""}{br.currency ? ` · ${br.currency}` : ""}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{tiers.length} tier{tiers.length === 1 ? "" : "s"}</span>
                </button>

                {isOpen && (
                  <div className="mt-3 ml-6 space-y-3">
                    {tiers.length === 0 && (
                      <p className="p-6 text-sm text-muted-foreground text-center border border-dashed border-border rounded-lg">No tiers yet. Try 100 pts = a free facial add-on.</p>
                    )}
                    {tiers.map((t, i) => (
                      <div key={i} className="grid grid-cols-[90px_1fr_1fr_auto] gap-2 items-center" data-testid={`tier-row-${br.id}-${i}`}>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-primary shrink-0" />
                          <Input type="number" value={t.points} onChange={(e) => updateTier(br.id, i, "points", Number(e.target.value) || 0)} data-testid={`tier-points-${br.id}-${i}`} />
                        </div>
                        <Input value={t.reward || ""} onChange={(e) => updateTier(br.id, i, "reward", e.target.value)} placeholder="Reward description" data-testid={`tier-reward-${br.id}-${i}`} />
                        <Input value={t.note || ""} onChange={(e) => updateTier(br.id, i, "note", e.target.value)} placeholder="Internal note (optional)" data-testid={`tier-note-${br.id}-${i}`} />
                        <Button size="icon" variant="ghost" onClick={() => removeTier(br.id, i)} data-testid={`remove-tier-${br.id}-${i}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                      </div>
                    ))}
                    <Button size="sm" onClick={() => addTier(br.id)} data-testid={`add-tier-btn-${br.id}`} className="gap-1"><Plus className="w-4 h-4" /> Add tier</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function BrandLoyalty() {  const [config, setConfig] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchCfgs, setBranchCfgs] = useState({}); // {branch_id: {idr_per_point}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        staffApi.get("/brand/loyalty"),
        staffApi.get("/branches"),
      ]);
      setConfig(c.data);
      setBranches(b.data || []);
      const cfgs = await Promise.all(
        (b.data || []).map((br) =>
          staffApi.get(`/branches/${br.id}/loyalty`).then((r) => [br.id, r.data]).catch(() => [br.id, { idr_per_point: 1000 }])
        )
      );
      setBranchCfgs(Object.fromEntries(cfgs));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const patch = (section, next) => {
    setConfig((prev) => ({ ...prev, [section]: { ...prev[section], ...next } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Force top-level defaults to 0 — the UI is per-branch-only so no
      // hidden "default" reward should leak in from older data.
      const body = {
        birthday: { enabled: !!config.birthday?.enabled, points: 0, branch_points: config.birthday?.branch_points || {} },
        join:     { enabled: !!config.join?.enabled,     points: 0, branch_points: config.join?.branch_points || {} },
        referral: { enabled: !!config.referral?.enabled, points: 0, branch_points: config.referral?.branch_points || {} },
        spend:    {
          enabled: !!config.spend?.enabled,
          currency_per_point: 0,
          branch_currency_per_point: config.spend?.branch_currency_per_point || {},
        },
        branch_enabled: config.branch_enabled || {},
      };
      const r = await staffApi.put("/brand/loyalty", body);
      setConfig(r.data);
      await Promise.all(
        branches.map((br) =>
          staffApi.put(`/branches/${br.id}/loyalty`, {
            enabled: !!(r.data.branch_enabled || {})[br.id],
            idr_per_point: Number((branchCfgs[br.id] || {}).idr_per_point) || 1000,
            tiers: (branchCfgs[br.id] || {}).tiers || [],
          })
        )
      );
      toast.success("Loyalty settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const enabledBranchCount = useMemo(
    () => Object.values(config?.branch_enabled || {}).filter(Boolean).length,
    [config?.branch_enabled]
  );

  if (loading || !config) {
    return (
      <StaffShell context="brand">
        <div className="p-6 text-sm text-muted-foreground">Loading loyalty settings…</div>
      </StaffShell>
    );
  }

  const pointsUnit = () => "points";
  const currencyUnit = (br) => `${br.currency || "IDR"} per point`;

  return (
    <StaffShell context="brand">
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="brand-loyalty-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Loyalty Rewards</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Configure how members earn points across the whole company. Every reward is set per branch so each currency prices its own value correctly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={reload} disabled={saving} data-testid="brand-loyalty-reload"><RefreshCw className="w-4 h-4 mr-1" />Reload</Button>
            <Button onClick={save} disabled={saving} data-testid="brand-loyalty-save" className="bg-slate-800 hover:bg-slate-900 text-white"><Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        </div>

        {/* Earn rules — every rule is per branch, no default. */}
        <Card>
          <CardContent className="p-5">
            <div className="uppercase text-xs tracking-wider text-slate-600 font-semibold mb-2">Earn rules · per branch</div>
            <BranchRule
              icon={Users}
              title="Membership Join Reward"
              description="Points credited when a guest signs up as a Member. The customer's home branch decides how many points they earn."
              rule={config.join}
              section="join"
              patch={patch}
              branches={branches}
              mapKey="branch_points"
              unitLabelForBranch={pointsUnit}
              testid="rule-join"
            />
            <BranchRule
              icon={Gift}
              title="Spend-to-Points"
              description="Every currency unit spent (after tax + service charge) earns fractional points. Set each branch's Earn Rate below in the Branch Point Values master table so Jakarta at 100,000 IDR/pt and Bahrain at 10 BHD/pt both work correctly at checkout."
              rule={config.spend}
              section="spend"
              patch={patch}
              branches={branches}
              mapKey="branch_currency_per_point"
              unitLabelForBranch={currencyUnit}
              testid="rule-spend"
              hideBranchInputs
            />
            <BranchRule
              icon={Cake}
              title="Birthday Reward"
              description="Awarded automatically on the member's first visit within a week of their birthday, once per year."
              rule={config.birthday}
              section="birthday"
              patch={patch}
              branches={branches}
              mapKey="branch_points"
              unitLabelForBranch={pointsUnit}
              testid="rule-birthday"
            />
            <BranchRule
              icon={Share2}
              title="Referral Reward"
              description="Locked to the referrer and only awarded after the referred guest's first invoice closes at that branch."
              rule={config.referral}
              section="referral"
              patch={patch}
              branches={branches}
              mapKey="branch_points"
              unitLabelForBranch={pointsUnit}
              testid="rule-referral"
            />
          </CardContent>
        </Card>

        {/* Brand-level Loyalty Expiry Warning email — single global template so
            every member sees the same look-and-feel regardless of branch. */}
        <BrandExpiryEmailCard />

        {/* Reward Tiers · per branch — the redemption ladder each location
            publishes to its members. Migrated from the deprecated Location
            Loyalty page so all loyalty settings live in one place. Persisted
            per-branch as `loyalty_configs.tiers`. */}
        <RewardTiersCard branches={branches} branchCfgs={branchCfgs} setBranchCfgs={setBranchCfgs} />

        {/* Branch Point Values — the single master table pairing each branch's
            local Earn Rate (currency per point earned) with its Redemption
            Value (currency per point discount at POS). Global balance is
            shared across branches; only the money math is local. */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="uppercase text-xs tracking-wider text-slate-600 font-semibold">Branch Point Values · earn & redeem per branch</div>
              <div className="text-xs text-muted-foreground" data-testid="brand-loyalty-enabled-count">{enabledBranchCount} of {branches.length} branches enabled</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
              Every guest has ONE global point balance. The values below decide how those points are earned and redeemed in each branch's local currency. Example: Bali at 100,000 IDR = 1 pt earned and 1 pt = 1,000 IDR off; Bahrain at 10 BHD = 1 pt earned and 1 pt = 0.1 BHD off. POS auto-applies the local rate.
            </p>
            {branches.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No branches yet.</p>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_120px] gap-4 px-2 pb-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>Branch</div>
                  <div>Earn Rate <span className="normal-case font-normal text-muted-foreground">(local $ per 1 pt earned)</span></div>
                  <div>Redemption Value <span className="normal-case font-normal text-muted-foreground">(local $ per 1 pt discount)</span></div>
                  <div className="text-right">Enabled</div>
                </div>
                <div className="divide-y divide-slate-100" data-testid="brand-loyalty-branch-table">
                  {branches.map((br) => {
                    const isOn = !!(config.branch_enabled || {})[br.id];
                    const earn = (config.spend?.branch_currency_per_point || {})[br.id] || 0;
                    const redeem = branchCfgs[br.id]?.idr_per_point || 1000;
                    return (
                      <div key={br.id} className="grid md:grid-cols-[1.4fr_1fr_1fr_120px] gap-4 items-start md:items-center py-3 px-2" data-testid={`brand-loyalty-branch-row-${br.id}`}>
                        <div>
                          <div className="font-semibold text-slate-800">{br.name}</div>
                          <p className="text-xs text-muted-foreground">{br.city || br.country || ""}{br.currency ? ` · ${br.currency}` : ""}</p>
                        </div>
                        <div>
                          <Label className="md:hidden text-[10px] uppercase tracking-wider text-muted-foreground">Earn Rate</Label>
                          <PointsInput
                            value={earn}
                            onChange={(v) => {
                              const next = { ...(config.spend?.branch_currency_per_point || {}) };
                              if (!v) delete next[br.id]; else next[br.id] = v;
                              patch("spend", { branch_currency_per_point: next });
                            }}
                            disabled={!isOn}
                            placeholder="0"
                            testid={`brand-loyalty-earn-rate-${br.id}`}
                          />
                          <p className="mt-1 text-[10px] text-muted-foreground">Spend {Number(earn || 0).toLocaleString()} {br.currency || "IDR"} to earn 1 pt.</p>
                        </div>
                        <div>
                          <Label className="md:hidden text-[10px] uppercase tracking-wider text-muted-foreground">Redemption Value</Label>
                          <PointsInput
                            value={redeem}
                            onChange={(v) => setBranchCfgs((prev) => ({ ...prev, [br.id]: { ...(prev[br.id] || {}), idr_per_point: v || 1000 } }))}
                            disabled={!isOn}
                            placeholder="1000"
                            testid={`brand-loyalty-idr-per-point-${br.id}`}
                          />
                          <p className="mt-1 text-[10px] text-muted-foreground">1 pt = {Number(redeem).toLocaleString()} {br.currency || "IDR"} off at POS.</p>
                        </div>
                        <div className="flex md:justify-end">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <Switch
                              checked={isOn}
                              onCheckedChange={(v) => setConfig((prev) => ({ ...prev, branch_enabled: { ...(prev.branch_enabled || {}), [br.id]: v } }))}
                              data-testid={`brand-loyalty-branch-toggle-${br.id}`}
                            />
                            <span className="text-xs text-muted-foreground">{isOn ? "On" : "Off"}</span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </StaffShell>
  );
}
